/**
 * Project types: the shape of the work inside a department. "New build" and "Renovation" are both
 * Field, but their phases, setup / cleanup checklists and what gets photographed differ. A type is a
 * template — tasks stay free-form; the type scaffolds the outline and runs the daily checklists.
 * A type can be nothing but a name and still collect data (see buildTypeStats). Pure — no I/O.
 *
 * Doc: { id, departmentId, name, icon, phases: [{ name }], setupSteps: [{ title, estimatedMinutes }],
 *        cleanupSteps: [...], closingPhase: { name, steps: [{ title }] } | null,
 *        capture: { beforePhotos, phasePhoto, discoveryLog }, shareDayReport, estimateMap: [],
 *        clonedFrom, createdAt,
 *        // CFO plan 4.2 — a type is the project template:
 *        jobType, tags: [], taskTemplates: [{ templateId, phase }], payDefault: { role: payType } | null,
 *        metrics: [metricId], estimateMap: [{ catalogItemId?, category?, templateId }] }
 * `taskTemplates` are refs to opsTaskTemplates (templates.js): the tasks a new project starts with, each in the phase
 * named. `estimateMap` sends a bid line (by catalog item, else by category) to a task template on import (BattleBids
 * planOpsImport).
 *
 * Setup and cleanup run as "bucket" tasks: ordinary tasks with `bucket: 'setup' | 'cleanup'` and
 * `bucketDate`, so time on them is recorded like any task but reported as its own bucket, and they
 * never appear in the plan, the outline or the card deck.
 */

export const BUCKETS = ['setup', 'cleanup'];

export function makeProjectType(overrides = {}) {
  return {
    departmentId: null,
    name: '',
    icon: '🧱',
    phases: [],
    setupSteps: [],
    cleanupSteps: [],
    closingPhase: null,
    capture: { beforePhotos: false, phasePhoto: false, discoveryLog: false },
    shareDayReport: false,
    estimateMap: [],
    clonedFrom: null,
    jobType: null,
    tags: [],
    taskTemplates: [],
    payDefault: null,
    metrics: [],
    parentId: null,        // a subtype inherits its parent and changes what differs (resolveType)
    anyDepartment: false,  // usable in any department (Oliver Shop is Personal and still Construction)
    dashboard: null,       // the project dashboard's work cards: { phaseMap, unit, watch } (projectdash.js)
    ...overrides,
  };
}

/** A copy to edit: new name, no id, remembers where it came from. */
export function cloneProjectType(type, name) {
  const { id, createdAt, updatedAt, ...rest } = type || {};
  return makeProjectType({ ...JSON.parse(JSON.stringify(rest)), name: name || `${type?.name || 'Type'} copy`, clonedFrom: id || null });
}

export function typeOf(project, types = []) {
  if (!project?.typeId) return null;
  return types.find((t) => t && t.id === project.typeId) || null;
}

/**
 * A type with its parents' settings under it: the subtype's own wins, a list it leaves empty comes from the parent.
 * Returns the type itself when it has no parent. A loop in parentId stops at the repeat.
 */
export function resolveType(type, types = []) {
  if (!type?.parentId) return type || null;
  const chain = [type]; const seen = new Set([type.id]);
  let cur = type;
  while (cur?.parentId && !seen.has(cur.parentId)) {
    cur = types.find((t) => t && t.id === cur.parentId) || null;
    if (!cur) break;
    seen.add(cur.id); chain.unshift(cur);
  }
  const empty = (v) => v == null || (Array.isArray(v) && !v.length);
  return chain.reduce((acc, t) => {
    const out = { ...acc };
    for (const [k, v] of Object.entries(t)) {
      if (k === 'dashboard') out.dashboard = { ...(acc.dashboard || {}), ...(v || {}) };
      else if (!empty(v)) out[k] = v;
    }
    return out;
  }, {});
}

/** "Construction › Roofing". */
export function typeLabel(type, types = []) {
  const parent = type?.parentId ? types.find((t) => t && t.id === type.parentId) : null;
  return parent ? `${parent.name} › ${type.name}` : String(type?.name || '');
}

/** Sorted for a picker: each parent, then its subtypes. */
export function typesInOrder(types = []) {
  const name = (t) => String(t?.name || '');
  const top = types.filter((t) => t && !t.parentId).sort((a, b) => name(a).localeCompare(name(b)));
  const known = new Set(top.map((t) => t.id));
  return [...top.flatMap((p) => [p, ...types.filter((t) => t && t.parentId === p.id).sort((a, b) => name(a).localeCompare(name(b)))]), ...types.filter((t) => t && t.parentId && !known.has(t.parentId))];
}

/** The types a project in this department can use: the department's, plus any department's. */
export function typesForDepartment(types = [], departmentId, generalId = 'general') {
  return typesInOrder(types.filter((t) => t && (t.anyDepartment || (t.departmentId || generalId) === (departmentId || generalId))));
}

/** Lj's starter set (Nov 1). Fixed ids, so adding them twice changes nothing. */
export const STARTER_PROJECT_TYPES = Object.freeze([
  { id: 'type-construction', name: 'Construction', icon: '🔨', anyDepartment: true, dashboard: { phaseMap: [
    { name: 'Set up', tags: ['mobilization', 'setup', 'pickup', 'supplies', 'planning'] }, { name: 'Demo', tags: ['demo', 'dump'] }, { name: 'Framing', tags: ['framing'] },
    { name: 'Envelope', tags: ['envelope', 'siding', 'soffit', 'stain'] }, { name: 'Interior', tags: ['insulation', 'drywall', 'mud', 'prep', 'paint', 'flooring', 'tile', 'trim'] }, { name: 'Close out', tags: ['cleanup', 'closeout'] },
  ] } },
  { id: 'type-roofing', parentId: 'type-construction', name: 'Roofing', icon: '🏠', anyDepartment: true, dashboard: {
    phaseMap: [
      { name: 'Set up', tags: ['mobilization', 'setup', 'pickup', 'supplies', 'planning'] }, { name: 'Tear-off + dry-in', tags: ['demo', 'envelope', 'dump'] }, { name: 'Shingles', tags: ['shingles'] },
      { name: 'Vents', tags: ['vents'] }, { name: 'Deck roof', tags: ['deck-roof'], first: true }, { name: 'Close out', tags: ['closeout', 'cleanup'] },
    ],
    unit: { label: 'Planes', short: 'P', prefix: 'plane-', sameDay: true, steps: [{ name: 'Demo', tag: 'demo' }, { name: 'Cover', tag: 'envelope' }] },
  } },
  { id: 'type-renovation', parentId: 'type-construction', name: 'Renovation', icon: '🛠️', anyDepartment: true },
  { id: 'type-new-build', parentId: 'type-construction', name: 'New build', icon: '🏗️', anyDepartment: true },
  { id: 'type-shop', name: 'Shop', icon: '🪚', anyDepartment: true },
  { id: 'type-service', name: 'Service', icon: '🚜', anyDepartment: true },
  { id: 'type-snow-removal', parentId: 'type-service', name: 'Snow removal', icon: '❄️', anyDepartment: true },
  { id: 'type-software', name: 'Software', icon: '💻', anyDepartment: true },
  { id: 'type-app', parentId: 'type-software', name: 'App', icon: '📱', anyDepartment: true, dashboard: { backlog: true } },
]);

/** The starter types not there yet, as full docs. */
export function missingStarterTypes(types = [], at = Date.now()) {
  const have = new Set(types.map((t) => t?.id));
  return STARTER_PROJECT_TYPES.filter((t) => !have.has(t.id)).map((t) => ({ ...makeProjectType(t), createdAt: at, updatedAt: at }));
}

export const isBucketTask = (t) => !!(t && t.bucket);

const cleanSteps = (list) => (Array.isArray(list) ? list : [])
  .map((s) => (typeof s === 'string' ? { title: s } : s))
  .filter((s) => s && String(s.title || '').trim())
  .map((s) => ({ title: String(s.title).trim(), estimatedMinutes: s.estimatedMinutes ? Number(s.estimatedMinutes) || null : null }));

/** The checklist for a bucket: the project's own list wins when it has one, else the type's. */
export function bucketSteps(project, type, bucket) {
  const key = `${bucket}Steps`;
  const own = cleanSteps(project?.[key]);
  return own.length ? own : cleanSteps(type?.[key]);
}

/**
 * What to write so today's checklist exists for a project. Unticked items from an earlier day are
 * carried to today (re-dated, not duplicated); steps already on today's list — open or done — are
 * not added again.
 * @returns {{ create: Array<object>, redate: Array<string> }}  create = task fields (no id)
 */
export function planBucketTasks({ project, type, bucket, date, tasks = [] }) {
  const steps = bucketSteps(project, type, bucket);
  if (!project?.id || !steps.length) return { create: [], redate: [] };
  const mine = tasks.filter((t) => t.projectId === project.id && t.bucket === bucket);
  const stale = mine.filter((t) => t.status !== 'done' && (t.bucketDate || '') < date);
  const today = mine.filter((t) => t.bucketDate === date);
  const have = new Set([...stale, ...today].map((t) => String(t.title || '').trim().toLowerCase()));
  const create = [];
  steps.forEach((s, i) => {
    if (have.has(s.title.toLowerCase())) return;
    create.push({
      title: s.title, projectId: project.id, sectionId: null, parentId: null,
      bucket, bucketDate: date, scheduledDate: date, estimatedMinutes: s.estimatedMinutes,
      priority: 'medium', sortOrder: (i + 1) * 1000,
    });
  });
  return { create, redate: stale.map((t) => t.id) };
}

/** Today's checklist for a project, in step order, with progress. */
export function bucketChecklist(tasks = [], projectId, bucket, date) {
  const list = tasks.filter((t) => t.projectId === projectId && t.bucket === bucket && t.bucketDate === date)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  return { list, done: list.filter((t) => t.status === 'done').length, total: list.length };
}

/** Sections a new project of this type starts with: its phases, then the closing phase. All active
 *  (Lj: "no locking, just numbers"). */
export function scaffoldSections(type) {
  const names = (type?.phases || []).map((p) => (typeof p === 'string' ? p : p?.name)).map((n) => String(n || '').trim()).filter(Boolean);
  if (type?.closingPhase?.name) names.push(String(type.closingPhase.name).trim());
  return names.map((title, i) => ({ title, order: i + 1, status: 'active' }));
}

/** Tasks the closing phase starts with (walkthrough, punch list…). */
export function closingSteps(type) {
  return cleanSteps(type?.closingPhase?.steps).map((s) => s.title);
}

const taskMs = (t) => {
  const e = Array.isArray(t.timeEntries) ? t.timeEntries : [];
  const sum = e.reduce((s, x) => s + (x?.duration || 0), 0);
  return sum || t.timeSpent || 0;
};

/**
 * What a type has learned from every project tagged with it — no extra tracking, all from data
 * already recorded.
 * @returns {{ projects, days, setupAvgMs, cleanupAvgMs, estimateAccuracy, phases: [{ name, avgMs, projects }], orderDrift, doneTasks }}
 */
export function buildTypeStats({ type, projects = [], tasks = [], sections = [] }) {
  const pids = new Set(projects.filter((p) => p.typeId === type?.id).map((p) => p.id));
  const mine = tasks.filter((t) => pids.has(t.projectId));
  const perDay = (bucket) => {
    const days = new Map();
    mine.filter((t) => t.bucket === bucket).forEach((t) => {
      const k = `${t.projectId}|${t.bucketDate || ''}`;
      days.set(k, (days.get(k) || 0) + taskMs(t));
    });
    const vals = [...days.values()].filter((v) => v > 0);
    return { avg: vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null, days: vals.length };
  };
  const setup = perDay('setup');
  const cleanup = perDay('cleanup');

  const work = mine.filter((t) => !t.bucket);
  const done = work.filter((t) => t.status === 'done');
  const est = done.filter((t) => t.estimatedMinutes && taskMs(t) > 0);
  const estMs = est.reduce((s, t) => s + t.estimatedMinutes * 60000, 0);
  const actMs = est.reduce((s, t) => s + taskMs(t), 0);

  const secById = new Map(sections.filter((s) => pids.has(s.projectId)).map((s) => [s.id, s]));
  const phaseMap = new Map();   // name → { ms, projects:Set }
  work.forEach((t) => {
    const sec = t.sectionId ? secById.get(t.sectionId) : null;
    if (!sec) return;
    const name = String(sec.title || '').trim();
    if (!phaseMap.has(name)) phaseMap.set(name, { ms: 0, projects: new Set(), order: sec.order || 0 });
    const ph = phaseMap.get(name);
    ph.ms += taskMs(t);
    ph.projects.add(t.projectId);
  });
  const phases = [...phaseMap.entries()]
    .map(([name, v]) => ({ name, avgMs: Math.round(v.ms / Math.max(1, v.projects.size)), projects: v.projects.size, order: v.order }))
    .sort((a, b) => a.order - b.order)
    .map(({ order, ...rest }) => rest);

  const ranked = done.filter((t) => t.completion?.plannedRank);
  const drift = ranked.length ? ranked.reduce((s, t) => s + (t.completion.plannedRank - 1), 0) / ranked.length : null;

  return {
    projects: pids.size,
    days: Math.max(setup.days, cleanup.days),
    setupAvgMs: setup.avg,
    cleanupAvgMs: cleanup.avg,
    estimateAccuracy: estMs > 0 ? Math.round((actMs / estMs) * 100) : null,
    phases,
    orderDrift: drift == null ? null : Math.round(drift * 10) / 10,
    doneTasks: done.length,
  };
}
